import i18nFactory from '@yandex-int/i18n';
import { get } from 'lodash';

import {
    MIN_CARDS_IN_COLLECTION,
    TEXT_BANNER_BODY_DEFAULT_MAX_LENGTH,
    TURBO_GALLERY_HREF_MAX_LENGTH
} from '../../../constants';
import { OrganizationErrorCodes } from '../../../declarations/organizations/organizations';
import { BannerId } from '../../../typings/banners';
import { ValidationResultItem, ValidationResultItemParamsPixel } from '../../../typings/common';
import { AdvType } from '../../../typings/domain';
import { NotReadyBanner } from '../../BannerEditValidation/helpers';
import { InternalBannerDefectCodes } from '../../BannerEditValidation/InternalBannerValidator';
import { createErrorsMapper, ErrorsMatcher } from '../../ErrorsMapper/ErrorsMapper';

import * as keyset from './ErrorsTextMapper.i18n';

const i18n = i18nFactory(keyset);

export const bannersTextErrorMapper = createErrorsMapper<{}>(
    ({ idx }): Array<ErrorsMatcher<{}>> => [
        {
            path: ['banners', idx],
            codes: [
                {
                    code: 'BannerDefectIds.Gen.INVALID_SITELINK_SET_USAGE',
                    message: (): string => {
                        return i18n('banner-invalid-sitelinkset-usage');
                    }
                },
                {
                    code: 'BannerDefectIds.Gen.IMAGE_SIZE_MODIFICATION',
                    message: (): string => {
                        return i18n('banner-image-size-modification');
                    }
                },
                {
                    code: 'BannerDefectIds.Gen.INCONSISTENT_BANNER_TYPE',
                    message: (): string => {
                        return i18n('banner-creative-required');
                    }
                },
                {
                    code: 'BannerDefectIds.Gen.REQUIRED_HREF_OR_VCARD_ID',
                    message: (): string => {
                        return i18n('banner-required-href-or-vcard');
                    }
                },
                {
                    code: 'BannerDefectIds.Gen.REQUIRED_HREF',
                    message: (): string => {
                        return i18n('banner-required-href');
                    }
                },
                {
                    code: 'BannerDefectIds.Gen.REQUIRED_HREF_OR_VCARD_ID_OR_TURBO',
                    message: (): string => {
                        return i18n('banner-required-href-or-vcard');
                    }
                },
                {
                    code: 'BannerDefectIds.Gen.REQUIRED_HREF_OR_VCARD_ID_OR_TURBO_OR_ORG',
                    message: (): string => {
                        return i18n('banner-required-href-turbo-vcard-organization');
                    }
                },
                {
                    code: 'BannerDefectIds.Gen.REQUIRED_HREF_OR_TURBOLANDING_ID',
                    message: (): string => {
                        return i18n('href-or-turbo-required');
                    }
                },
                {
                    code: 'BannerDefectIds.Gen.CANNOT_UPDATE_ARCHIVED_AD',
                    message: (): string => {
                        return i18n('banner-connot-update-archived');
                    }
                },
                {
                    code: 'BannerDefectIds.LanguageDefect.INCONSISTENT_LANGUAGE_WITH_GEO',
                    message: (err: ValidationResultItem): string => {
                        switch (err.params) {
                            case 'KAZAKH':
                                return i18n('banners-text-language-inconsistent-with-geo-kazakh');
                            case 'UKRAINE':
                                return i18n('banners-text-language-inconsistent-with-geo-ukraine');
                            case 'TURKISH':
                                return i18n('banners-text-language-inconsistent-with-geo-turkish');
                            case 'BELARUSIAN':
                                return i18n('banners-text-language-inconsistent-with-geo-belarusian');
                            case 'UZBEK':
                                return i18n('banners-text-language-inconsistent-with-geo-uzbek');
                            default:
                                return i18n('banners-text-language-inconsistent-with-geo-default');
                        }
                    }
                }
            ]
        },
        {
            path: ['banners', idx, 'title'],
            codes: [
                {
                    code: 'DefectIds.CANNOT_BE_NULL',
                    message: (): string => i18n('cannot-be-empty')
                },
                {
                    code: 'BannerDefectIds.String.SHOULD_NOT_BE_BLANK',
                    message: (): string => {
                        return i18n('title-should-not-be-blank');
                    }
                },
                {
                    code: 'BannerDefectIds.Gen.RESTRICTED_CHARS_IN_FIELD',
                    message: (): string => {
                        return i18n('title-restricted-chars-in-field');
                    }
                },
                {
                    code: 'BannerDefectIds.String.TEXT_LENGTH_WITHOUT_TEMPLATE_MARKER_CANNOT_BE_MORE_THAN_MAX',
                    message: (err: ValidationResultItem): string => {
                        return i18n('title-max-length', {
                            maxLength: get(err, 'params.maxLength', '')
                        });
                    }
                },
                {
                    code: 'BannerDefectIds.String.LENGTH_WORD_WITHOUT_TEMPLATE_MARKER_CANNOT_BE_MORE_THAN_MAX',
                    message: (err: ValidationResultItem): string => {
                        return i18n('title-max-length-word', {
                            maxLength: get(err, 'params.maxLength', '')
                        });
                    }
                },
                {
                    code: 'BannerDefectIds.String.MAX_NUMBER_OF_NARROW_CHARACTERS_CANNOT_BE_MORE_THAN_MAX',
                    message: (err: ValidationResultItem): string => {
                        return i18n('title-max-narrow-length', {
                            maxLength: get(err, 'params.maxLength', '')
                        });
                    }
                },
                {
                    code: 'BannerDefectIds.Gen.INVALID_TEMPLATE_QUERY',
                    message: (): string => {
                        return i18n('title-invalid-template-query');
                    }
                }
            ]
        },
        {
            path: ['banners', idx, 'titleExtension'],
            codes: [
                {
                    code: 'BannerDefectIds.Gen.RESTRICTED_CHARS_IN_FIELD',
                    message: (): string => {
                        return i18n('title-restricted-chars-in-field');
                    }
                },
                {
                    code: 'BannerDefectIds.String.TEXT_LENGTH_WITHOUT_TEMPLATE_MARKER_CANNOT_BE_MORE_THAN_MAX',
                    message: (err: ValidationResultItem): string => {
                        return i18n('title-max-length', {
                            maxLength: get(err, 'params.maxLength', '')
                        });
                    }
                },
                {
                    code: 'BannerDefectIds.String.LENGTH_WORD_WITHOUT_TEMPLATE_MARKER_CANNOT_BE_MORE_THAN_MAX',
                    message: (err: ValidationResultItem): string => {
                        return i18n('title-max-length-word', {
                            maxLength: get(err, 'params.maxLength', '')
                        });
                    }
                },
                {
                    code: 'BannerDefectIds.String.MAX_NUMBER_OF_NARROW_CHARACTERS_CANNOT_BE_MORE_THAN_MAX',
                    message: (err: ValidationResultItem): string => {
                        return i18n('title-max-narrow-length', {
                            maxLength: get(err, 'params.maxLength', '')
                        });
                    }
                },
                {
                    code: 'BannerDefectIds.Gen.INVALID_TEMPLATE_QUERY',
                    message: (): string => {
                        return i18n('title-invalid-template-query');
                    }
                }
            ]
        },
        {
            path: ['banners', idx, 'body'],
            codes: [
                {
                    code: 'DefectIds.CANNOT_BE_NULL',
                    message: (): string => i18n('cannot-be-empty')
                },
                {
                    code: 'BannerDefectIds.String.SHOULD_NOT_BE_BLANK',
                    message: (): string => {
                        return i18n('body-should-not-be-blank');
                    }
                },
                {
                    code: 'BannerDefectIds.Gen.RESTRICTED_CHARS_IN_FIELD',
                    message: (): string => {
                        return i18n('body-restricted-chars-in-field');
                    }
                },
                {
                    code: 'BannerDefectIds.String.TEXT_LENGTH_WITHOUT_TEMPLATE_MARKER_CANNOT_BE_MORE_THAN_MAX',
                    message: (err: ValidationResultItem): string => {
                        return i18n('body-max-length', {
                            count: get(err, 'params.maxLength', TEXT_BANNER_BODY_DEFAULT_MAX_LENGTH)
                        });
                    }
                },
                {
                    code: 'BannerDefectIds.String.LENGTH_WORD_WITHOUT_TEMPLATE_MARKER_CANNOT_BE_MORE_THAN_MAX',
                    message: (err: ValidationResultItem): string => {
                        return i18n('body-max-world-length', {
                            maxLength: get(err, 'params.maxLength', '')
                        });
                    }
                },
                {
                    code: 'BannerDefectIds.String.MAX_NUMBER_OF_NARROW_CHARACTERS_CANNOT_BE_MORE_THAN_MAX',
                    message: (err: ValidationResultItem): string => {
                        return i18n('body-max-narrow-length', {
                            maxLength: get(err, 'params.maxLength', '')
                        });
                    }
                },
                {
                    code: 'BannerDefectIds.Gen.INVALID_TEMPLATE_QUERY',
                    message: (): string => {
                        return i18n('body-invalid-template-query');
                    }
                }
            ]
        },
        {
            path: ['banners', idx, 'href'],
            codes: [
                {
                    code: 'BannerDefectIds.Gen.EMPTY_HREF',
                    message: (): string => {
                        return i18n('href-should-not-be-blank');
                    }
                },
                {
                    code: 'StringDefectIds.CANNOT_BE_EMPTY',
                    message: (): string => {
                        return i18n('href-should-not-be-blank');
                    }
                },
                {
                    code: 'DefectIds.CANNOT_BE_NULL',
                    message: (): string => {
                        return i18n('href-should-not-be-blank');
                    }
                },
                {
                    code: 'DefectIds.INVALID_VALUE',
                    message: (): string => {
                        return i18n('href-invalid-value');
                    }
                },
                {
                    code: 'DefectIds.INVALID_HREF',
                    message: (): string => {
                        return i18n('href-invalid-value');
                    }
                },
                {
                    code: 'BannerDefectIds.String.HREF_LENGTH_WITHOUT_TEMPLATE_MARKER_CANNOT_BE_MORE_THAN_MAX',
                    message: (err: ValidationResultItem): string => {
                        return i18n('href-max-length', {
                            maxLength: get(err, 'params.maxLength', '')
                        });
                    }
                },
                {
                    code: 'BannerDefectIds.Gen.INVALID_HREF',
                    message: (): string => {
                        return i18n('href-invalid-value');
                    }
                }
            ]
        },
        {
            path: ['banners', idx, 'domain'],
            codes: [
                {
                    code: 'DefectIds.INVALID_VALUE',
                    message: (): string => {
                        return i18n('domain-invalid-value');
                    }
                }
            ]
        },
        {
            path: ['banners', idx, 'displayHref'],
            codes: [
                {
                    code: 'BannerDefectIds.String.DISPLAY_HREF_LENGTH_CANNOT_BE_MORE_THAN_MAX',
                    message: (err: ValidationResultItem): string => {
                        return i18n('displayhref-max-length', {
                            maxLength: get(err, 'params.maxLength', '')
                        });
                    }
                },
                {
                    code: 'BannerDefectIds.Gen.INVALID_TEMPLATE_QUERY',
                    message: (): string => {
                        return i18n('display-href-invalid-template-query');
                    }
                }
            ]
        },
        {
            path: ['banners', idx, 'turboHrefParams'],
            codes: [
                {
                    code: 'StringDefectIds.LENGTH_CANNOT_BE_MORE_THAN_MAX',
                    message: (err: ValidationResultItem): string => {
                        return i18n('turbo-href-params-length', {
                            maxLength: get(err, 'params.maxLength', '')
                        });
                    }
                }
            ]
        },
        {
            path: ['banners', idx, 'callouts'],
            codes: [
                {
                    code: 'BannerDefectIds.Size.MAX_CALLOUTS_PER_BANNER',
                    message: (err: ValidationResultItem): string => {
                        return i18n('callouts-max-size', {
                            maxSize: get(err, 'params.maxSize', '')
                        });
                    }
                },
                {
                    code: 'BannerDefectIds.Size.MAX_CALLOUTS_ON_BANNER_ADD',
                    message: (err: ValidationResultItem): string => {
                        return i18n('callouts-max-size', {
                            maxSize: get(err, 'params.maxSize', '')
                        });
                    }
                }
            ]
        },
        {
            path: ['banners', idx, 'turboGalleryHref'],
            codes: [
                {
                    code: 'StringDefectIds.LENGTH_CANNOT_BE_MORE_THAN_MAX',
                    message: (err: ValidationResultItem): string => {
                        return i18n('turbo-gallery-href-max-length', {
                            count: get(err, 'params.maxLength', TURBO_GALLERY_HREF_MAX_LENGTH)
                        });
                    }
                },
                {
                    code: 'BannerDefectIds.Gen.INVALID_HREF',
                    message: (): string => i18n('href-invalid-value')
                },
                {
                    code: 'BannerDefectIds.Gen.EMPTY_HREF',
                    message: (): string => {
                        return i18n('href-should-not-be-blank');
                    }
                }
            ]
        },
        {
            path: ['banners', idx, 'contentPromotionUrl'],
            codes: [
                {
                    code: 'DefectIds.OBJECT_NOT_FOUND',
                    message: (): string => {
                        return i18n('invalid-link');
                    }
                },
                {
                    code: 'DefectIds.MUST_BE_VALID_ID',
                    message: (): string => i18n('cannot-be-empty')
                },
                {
                    code: 'BannerDefectIds.Gen.EMPTY_HREF',
                    message: (): string => i18n('cannot-be-empty')
                },
                {
                    code: 'BannerDefectIds.Gen.INVALID_HREF',
                    message: (): string => i18n('href-invalid-value')
                },
                {
                    code: 'ContentPromotionDefectIds.NumberDefectIds.NOT_ENOUGH_CARDS_IN_NEW_COLLECTION',
                    message: (err: ValidationResultItem): string =>
                        i18n('not-enough-cards-in-collection', {
                            count: get(err, 'params.min', MIN_CARDS_IN_COLLECTION)
                        })
                },
                {
                    code: 'ContentPromotionDefectIds.GeneralDefectIds.CONTENT_PROMOTION_NOT_FOUND',
                    message: (err: ValidationResultItem): string => {
                        switch (get(err, 'params.bannerType')) {
                            case AdvType.CONTENT_PROMOTION_VIDEO:
                                return i18n('invalid-promotion-video-link');
                            case AdvType.CONTENT_PROMOTION_COLLECTION:
                                return i18n('invalid-promotion-collection-link');
                            default:
                                return i18n('invalid-link');
                        }
                    }
                },
                {
                    code: 'ContentPromotionDefectIds.GeneralDefectIds.CONTENT_PROMOTION_INACCESSIBLE',
                    message: (err: ValidationResultItem): string => {
                        switch (get(err, 'params.bannerType')) {
                            case AdvType.CONTENT_PROMOTION_VIDEO:
                                return i18n('video-is-inaccessible');
                            case AdvType.CONTENT_PROMOTION_COLLECTION:
                                return i18n('collection-is-inaccessible');
                            default:
                                return i18n('invalid-link');
                        }
                    }
                },
                {
                    code: 'BannerDefectIds.Gen.CONTENT_PROMOTION_COLLECTION_IS_INACCESSIBLE',
                    message: (): string => i18n('collection-is-inaccessible')
                },
                {
                    code: 'BannerDefectIds.Gen.CONTENT_PROMOTION_VIDEO_IS_INACCESSIBLE',
                    message: (): string => i18n('video-is-inaccessible')
                }
            ]
        },
        {
            path: ['banners', idx, 'image'],
            codes: [
                {
                    code: 'BannerDefectIds.Gen.IMAGE_NOT_FOUND',
                    message: (): string => {
                        return i18n('banner-creative-required');
                    }
                }
            ]
        },
        {
            path: ['banners', idx, 'creativeId'],
            codes: [
                {
                    code: 'DefectIds.CANNOT_BE_NULL',
                    message: (): string => {
                        return i18n('banner-creative-required');
                    }
                },
                {
                    code: 'DefectIds.INCONSISTENT_CREATIVE_TYPE_TO_BANNER_TYPE',
                    message: (): string => {
                        return i18n('banner-creative-not-supported');
                    }
                }
            ]
        },
        {
            path: ['banners', idx, 'pixels'],
            codes: [
                {
                    code: 'BannerDefectIds.PixelProviderLimits.EQUAL_PIXEL_PROVIDERS',
                    message: (error: ValidationResultItem): string => {
                        const exceededClientPixelProviderNames =
                            get(error, 'params.exceededClientPixelProviderNames') || [];

                        return i18n('equal-pixels', {
                            providerNames: `${exceededClientPixelProviderNames.join(', ')}`
                        });
                    }
                }
            ]
        },
        {
            path: ['banners', idx, 'pixels', idx],
            codes: [
                {
                    code: 'BannerDefectIds.Gen.INVALID_PIXEL_FORMAT',
                    message: (): string => {
                        return i18n('incorrect-pixel-url');
                    }
                },
                {
                    code: 'BannerDefectIds.PixelPermissions.NO_RIGHTS_TO_PIXEL',
                    message: (error: ValidationResultItem): string => {
                        const {
                            accessibleClientProviderNames: rights = []
                        } = error.params as ValidationResultItemParamsPixel;
                        const lastRight = rights.pop();

                        if (rights.length === 0) {
                            return i18n('no-right-to-pixel', {
                                counters: lastRight
                            });
                        }

                        return i18n('no-right-to-pixel', {
                            counters: `${rights.join(', ')} ${i18n('or')} ${lastRight}`
                        });
                    }
                },
                {
                    code: 'GridDefectIds.Pixels.INVALID_AUDIT_PIXEL_FORMAT',
                    message: (): string => {
                        return i18n('incorrect-pixel-url');
                    }
                },
                {
                    code: 'GridDefectIds.Pixels.INVALID_AUDIENCE_PIXEL_FORMAT',
                    message: (): string => {
                        return i18n('incorrect-ya-auditory-pixel-url');
                    }
                }
            ]
        },
        {
            path: ['banners', idx, 'turboLandingId'],
            codes: [
                {
                    code: 'DefectIds.CANNOT_BE_NULL',
                    message: (): string => {
                        return i18n('turbo-required');
                    }
                }
            ]
        },
        {
            path: ['banners', idx, 'measurers', idx],
            codes: [
                {
                    code: 'DefectIds.INVALID_VALUE',
                    message: (): string => {
                        return i18n('incorrect-measures');
                    }
                },
                {
                    code: 'DefectIds.CANNOT_BE_NULL',
                    message: (): string => {
                        return i18n('incorrect-measures');
                    }
                }
            ]
        },
        {
            path: ['banners', idx, 'tnsId'],
            codes: [
                {
                    code: 'BannerDefectIds.Gen.RESTRICTED_CHARS_IN_FIELD',
                    message: (): string => {
                        return i18n('tns-id-restricted-characters');
                    }
                },
                {
                    code: 'StringDefectIds.CANNOT_BE_EMPTY',
                    message: (): string => {
                        return i18n('tns-id-cannot-be-empty');
                    }
                },
                {
                    code: 'StringDefectIds.LENGTH_CANNOT_BE_MORE_THAN_MAX',
                    message: (err: ValidationResultItem): string => {
                        return i18n('tns-id-length', {
                            maxLength: get(err, 'params.maxLength', '')
                        });
                    }
                }
            ]
        },
        {
            path: ['banners', idx, 'organization'],
            codes: [
                {
                    code: OrganizationErrorCodes.NOT_FOUND,
                    message: (): string => {
                        return i18n('organization-not-found');
                    }
                }
            ]
        }
    ],
    error => i18n('unknown-error-text')
);

export const internalBannersErrorsMapper = createErrorsMapper<{}>(
    ({ idx }) => [
        {
            path: ['banners', idx],
            codes: [
                {
                    code: InternalBannerDefectCodes.MAX_BANNERS_IN_ADGROUP,
                    message: (): string => i18n('unknown-error-text')
                }
            ]
        },
        {
            path: ['banners', idx, 'templateVars'],
            codes: [
                {
                    code: InternalBannerDefectCodes.REQUIRED_BUT_EMPTY,
                    message: (): string => i18n('cannot-be-empty')
                },
                {
                    code: InternalBannerDefectCodes.TEXT_TOO_LONG,
                    message: (err): string =>
                        i18n('description-cannot-be-more-than-max', {
                            maxLength: get(err, 'params.maxLength', '')
                        })
                },
                {
                    code: InternalBannerDefectCodes.TEXT_TOO_SHORT,
                    message: (err): string =>
                        i18n('description-cannot-be-less-than-max', {
                            minLength: get(err, 'params.minLength', '')
                        })
                },
                {
                    code: InternalBannerDefectCodes.URL_NOT_VALID,
                    message: (err): string =>
                        get(err, 'params.isHttpsOnly', false)
                            ? i18n('href-invalid-or-not-https-scheme')
                            : i18n('href-invalid-or-no-scheme')
                },
                {
                    code: InternalBannerDefectCodes.INVALID_CHOICE_VALUE,
                    message: (): string => i18n('invalid-choice-value')
                },
                {
                    code: InternalBannerDefectCodes.MUST_BE_EMPTY,
                    message: (): string => i18n('must-be-empty')
                }
            ]
        },
        {
            path: ['banners', idx, 'description'],
            codes: [
                {
                    code: InternalBannerDefectCodes.LENGTH_CANNOT_BE_MORE_THAN_MAX,
                    message: (err): string =>
                        i18n('description-cannot-be-more-than-max', {
                            maxLength: get(err, 'params.maxLength', '')
                        })
                },
                {
                    code: InternalBannerDefectCodes.CANNOT_BE_EMPTY,
                    message: (err): string => i18n('cannot-be-empty')
                }
            ]
        },
        {
            path: ['banners', idx, 'templateId'],
            codes: [
                {
                    code: InternalBannerDefectCodes.MUST_BE_VALID_ID,
                    message: (err): string => i18n('choose-correct-template')
                },
                {
                    code: InternalBannerDefectCodes.INTERNAL_TEMPLATE_NOT_ALLOWED,
                    message: (err): string => i18n('template-not-allowed')
                }
            ]
        }
    ],
    error => i18n('unknown-error-text')
);

export const internalImageUploadErrorMapper = createErrorsMapper<{}>(
    ({ idx }) => [
        {
            path: [],
            codes: [
                {
                    code: 'NO_DUPLICATE_MASS_ACTIONS',
                    message: (): string => {
                        return i18n('duplicate-error');
                    }
                },
                {
                    code: 'StringDefectIds.CANNOT_BE_EMPTY',
                    message: (): string => {
                        return i18n('href-null');
                    }
                },
                {
                    code: 'DefectIds.INVALID_FORMAT',
                    message: (): string => {
                        return i18n('image-invalid-format');
                    }
                },
                {
                    code: 'ImageDefectIds.Gen.IMAGE_SIZE_IS_ALLOWED',
                    message: (): string => {
                        return i18n('image-invalid-sizes');
                    }
                },
                {
                    code: 'ImageDefectIds.Gen.IMAGE_SIZE_IS_NOT_ALLOWED',
                    message: (): string => {
                        return i18n('image-invalid-sizes');
                    }
                },
                {
                    code: 'FILE_SIZE_LIMIT',
                    message: (err: ValidationResultItem): string => {
                        return i18n('image-invalid-file-size-param', {
                            maxSize: get(err, 'params.maxSize', '')
                        });
                    }
                },
                {
                    code: 'DefectIds.INVALID_VALUE',
                    message: (): string => {
                        return i18n('href-invalid-value');
                    }
                },
                {
                    code: 'ImageDefectIds.Gen.INVALID_IMAGE_URL',
                    message: (): string => {
                        return i18n('href-invalid');
                    }
                },
                {
                    code: 'DefectIds.CANNOT_BE_NULL',
                    message: (): string => {
                        return i18n('href-null');
                    }
                },
                {
                    code: 'ImageDefectIds.Gen.INVALID_IMAGE_FILE',
                    message: (): string => {
                        return i18n('href-file-invalid');
                    }
                },
                {
                    code: 'ImageDefectIds.Gen.IMAGE_MIME_TYPE_IS_NOT_SUPPORTED',
                    message: (): string => {
                        return i18n('href-file-format-invalid');
                    }
                },
                {
                    code: 'ImageDefectIds.Gen.IMAGE_FILE_SIZE_GREATER_THAN_MAX',
                    message: (): string => {
                        return i18n('href-image-file-size-invalid');
                    }
                },
                {
                    code: 'BannerDefectIds.Gen.IMAGE_SIZE_MODIFICATION',
                    message: (err: ValidationResultItem): string => {
                        return i18n('banner-image-size-modification-with-size', {
                            size: get(err, 'params.size', '')
                        });
                    }
                },
                {
                    code: 'BannerDefectIds.Gen.IMAGE_SIZE_MODIFICATION_ADAPTIVE',
                    message: (): string => {
                        return i18n('banner-image-size-modification-adaptive');
                    }
                },
                {
                    code: 'GIF_FILE_ERROR',
                    message: (): string => {
                        return i18n('gif-file-error-text');
                    }
                }
            ]
        },
        {
            path: ['banners', idx, 'templateVars'],
            codes: [
                {
                    code: InternalBannerDefectCodes.REQUIRED_BUT_EMPTY,
                    message: (): string => i18n('banner-image-required')
                },
                {
                    code: InternalBannerDefectCodes.IMAGE_FORMAT_INVALID,
                    message: (err): string =>
                        i18n('image-invalid-formats', {
                            formats: (get(err, 'params.formats', []) as string[]).join(', ')
                        })
                },
                {
                    code: InternalBannerDefectCodes.IMAGE_SIZE_KB_TOO_BIG,
                    message: (err): string =>
                        i18n('image-invalid-file-size-param', {
                            maxSize: get(err, 'params.maxSizeKb', '')
                        })
                },
                {
                    code: InternalBannerDefectCodes.IMAGE_DIMENSION_NOT_EQUAL,
                    message: (err): string => {
                        const width = get(err, 'params.width', '');
                        const height = get(err, 'params.height', '');

                        return width && height
                            ? i18n('image-not-equal-width-height', {
                                  width,
                                  height
                              })
                            : width
                            ? i18n('image-not-equal-width', { width })
                            : height
                            ? i18n('image-not-equal-height', { height })
                            : i18n('image-not-equal');
                    }
                },
                {
                    code: InternalBannerDefectCodes.IMAGE_DIMENSION_TOO_BIG,
                    message: (err): string => {
                        const width = get(err, 'params.width', '');
                        const height = get(err, 'params.height', '');

                        return width && height
                            ? i18n('image-too-big-width-height', {
                                  width,
                                  height
                              })
                            : width
                            ? i18n('image-too-big-width', { width })
                            : height
                            ? i18n('image-too-big-height', { height })
                            : i18n('image-too-big');
                    }
                },
                {
                    code: InternalBannerDefectCodes.IMAGE_DIMENSION_TOO_SMALL,
                    message: (err): string => {
                        const width = get(err, 'params.width', '');
                        const height = get(err, 'params.height', '');

                        return width && height
                            ? i18n('image-too-small-width-height', {
                                  width,
                                  height
                              })
                            : width
                            ? i18n('image-too-small-width', { width })
                            : height
                            ? i18n('image-too-small-height', { height })
                            : i18n('image-too-small');
                    }
                }
            ]
        }
    ],
    (): string => i18n('unknown-error-text')
);

export const calloutsTextErrorMapper = createErrorsMapper<{}>(
    ({ idx }) => [
        {
            path: [],
            codes: [
                {
                    code: 'API_ERROR',
                    message: (): string => {
                        return i18n('callout-unknown-error');
                    }
                }
            ]
        },
        {
            path: ['calloutIds', idx],
            codes: [
                {
                    code: 'CalloutDefectIds.Gen.AD_EXTENSION_IS_IN_USE',
                    message: (): string => {
                        return i18n('callout-used');
                    }
                }
            ]
        },
        {
            path: ['saveItems', idx, 'text'],
            codes: [
                {
                    code: 'CalloutDefectIds.Strings.CALLOUT_TEXT_LENGTH_EXCEEDED',
                    message: (): string => {
                        return i18n('callout-text-length-exceeded');
                    }
                },
                {
                    code: 'StringDefectIds.CANNOT_BE_EMPTY',
                    message: (): string => {
                        return i18n('callout-text-cannot-be-empty');
                    }
                },
                {
                    code: 'CalloutDefectIds.Strings.ALLOWED_SYMBOLS_CALLOUT_TEXT',
                    message: (): string => {
                        return i18n('callout-text-not-allowed-symbols');
                    }
                }
            ]
        }
    ],
    error => i18n('unknown-error-text')
);

export const imageGalleryTextErrorMapper = createErrorsMapper<{}>(
    ({ idx }) => [
        {
            path: [],
            codes: [
                {
                    code: 'IMAGES_COUNT_NULL',
                    message: (): string => {
                        return i18n('image-gallery-empty-images');
                    }
                }
            ]
        }
    ],
    error => i18n('unknown-error-header')
);

export const imageUploadTextErrorMapper = createErrorsMapper<{}>(
    ({ idx }) => [
        {
            path: [],
            codes: [
                {
                    code: 'NO_DUPLICATE_MASS_ACTIONS',
                    message: (): string => {
                        return i18n('duplicate-error');
                    }
                },
                {
                    code: 'StringDefectIds.CANNOT_BE_EMPTY',
                    message: (): string => {
                        return i18n('href-null');
                    }
                },
                {
                    code: 'DefectIds.INVALID_FORMAT',
                    message: (): string => {
                        return i18n('image-invalid-format');
                    }
                },
                {
                    code: 'ImageDefectIds.Gen.IMAGE_SIZE_IS_ALLOWED',
                    message: (): string => {
                        return i18n('image-invalid-sizes');
                    }
                },
                {
                    code: 'ImageDefectIds.Gen.IMAGE_SIZE_IS_NOT_ALLOWED',
                    message: (): string => {
                        return i18n('image-invalid-sizes');
                    }
                },
                {
                    code: 'ImageDefectIds.Gen.IMAGE_SIZE_IS_NOT_ALLOWED_MC_BANNER',
                    message: (): string => {
                        return i18n('image-invalid-sizes-mc-banner');
                    }
                },
                {
                    code: 'FILE_SIZE_LIMIT',
                    message: (err: ValidationResultItem): string => {
                        return i18n('image-invalid-file-size-param', {
                            maxSize: get(err, 'params.maxSize', '')
                        });
                    }
                },
                {
                    code: 'DefectIds.INVALID_VALUE',
                    message: (): string => {
                        return i18n('href-invalid-value');
                    }
                },
                {
                    code: 'ImageDefectIds.Gen.INVALID_IMAGE_URL',
                    message: (): string => {
                        return i18n('href-invalid');
                    }
                },
                {
                    code: 'DefectIds.CANNOT_BE_NULL',
                    message: (): string => {
                        return i18n('href-null');
                    }
                },
                {
                    code: 'ImageDefectIds.Gen.INVALID_IMAGE_FILE',
                    message: (): string => {
                        return i18n('href-file-invalid');
                    }
                },
                {
                    code: 'ImageDefectIds.Gen.IMAGE_MIME_TYPE_IS_NOT_SUPPORTED',
                    message: (): string => {
                        return i18n('href-file-format-invalid');
                    }
                },
                {
                    code: 'ImageDefectIds.Gen.IMAGE_FILE_SIZE_GREATER_THAN_MAX',
                    message: (): string => {
                        return i18n('href-image-file-size-invalid');
                    }
                },
                {
                    code: 'BannerDefectIds.Gen.IMAGE_SIZE_MODIFICATION',
                    message: (err: ValidationResultItem): string => {
                        return i18n('banner-image-size-modification-with-size', {
                            size: get(err, 'params.size', '')
                        });
                    }
                },
                {
                    code: 'BannerDefectIds.Gen.IMAGE_SIZE_MODIFICATION_ADAPTIVE',
                    message: (): string => {
                        return i18n('banner-image-size-modification-adaptive');
                    }
                },
                {
                    code: 'GIF_FILE_ERROR',
                    message: (): string => {
                        return i18n('gif-file-error-text');
                    }
                }
            ]
        }
    ],
    (): string => i18n('unknown-error-text')
);

export type BannerSummaryErrorParams = Record<BannerId, NotReadyBanner>;

export const getNotReadyToSaveBannersParams = (banners: NotReadyBanner[]): BannerSummaryErrorParams => {
    return banners.reduce((result, banner) => {
        result[banner.id] = banner;

        return result;
    }, {});
};
